import {
  FileUploadStatus,
  IFileUploadStatuses,
  IUploadRecord,
} from '@/components/MassUpload/MassUpload.types';
import {
  calculateUploadProgress,
  endpointsList,
  getTotalFormDataSize,
  toFormData,
} from '@/helpers';
import axios, { AxiosProgressEvent } from 'axios';
import {
  DocumentKind,
  IFileUploadUpdateDto,
  IUploadUpdateDependenciesDto,
} from 'types';
import { isDefined } from 'utils';
import { v4 as uuidv4 } from 'uuid';
import { computed, ref, Ref } from 'vue';

type FormDataForUpload = {
  documentOid: string;
  documentId: string;
  file: FormData;
  renditionFile?: FormData;
};

export function useUploadUpdate(
  uploadRecords: Ref<IUploadRecord[]>,
  uploadUpdateDependencies: Ref<IUploadUpdateDependenciesDto | undefined>,
) {
  const uploadCorrelationId = uuidv4();
  const fileUploadStatuses = ref<IFileUploadStatuses>({});
  const totalFileSize = ref(0);
  const remainingFileSize = ref(0);

  const uploadProgress = computed(() =>
    calculateUploadProgress(totalFileSize.value, remainingFileSize.value),
  );

  async function uploadFilesAndFinilize(): Promise<void> {
    await uploadFiles();
    await finishUploadUpdate();
  }

  async function uploadFiles(): Promise<void> {
    const formData = transformUploadRecordsToFormData();
    await setTotalFileSize(formData);

    for (const data of formData) {
      fileUploadStatuses.value[data.documentId] = FileUploadStatus.UPLOADING;
      await Promise.all(
        [data.file, data.renditionFile].map(async (fileData) => {
          /** Rendition file can be undefined */
          if (!fileData) return;
          await axios.post(
            endpointsList.uploadHandler.uploadUpdateHandler.uploadFile(
              data.documentOid,
            ),
            fileData,
            {
              onUploadProgress: (e: AxiosProgressEvent) => {
                remainingFileSize.value = remainingFileSize.value - e.bytes;
              },
            },
          );
        }),
      );
      fileUploadStatuses.value[data.documentId] = FileUploadStatus.UPLOADED;
    }
  }

  async function finishUploadUpdate(): Promise<void> {
    await axios.post(
      endpointsList.uploadHandler.uploadUpdateHandler.finalize(
        uploadCorrelationId,
      ),
    );
  }

  /**
   * Transforming all upload records with files to form data before starting
   * the upload to be able to calculate the total data size to be uploaded.
   */
  function transformUploadRecordsToFormData(): FormDataForUpload[] {
    return uploadRecords.value
      .map((record) => {
        const documentOid = getDocumentOidByOriginalFileName(record.file!.name);
        const [file, renditionFile] = [record, record.renditionFile].map(
          (record, index) => {
            /** The rendition file is optional, so we check if it exists */
            if (!record?.file?.size) return;
            return toFormData<IFileUploadUpdateDto & { file: File }>({
              uploadCorrelationId: uploadCorrelationId,
              file: record.file,
              documentType:
                index === 0 ? DocumentKind.MAIN : DocumentKind.RENDITION,
            });
          },
        );
        return {
          documentOid,
          documentId: record.metadata!.documentId,
          file: file!,
          renditionFile: renditionFile,
        };
      })
      .filter(isDefined);
  }

  /**
   * Calculating the total file size of all files to be uploaded for tracking
   * upload progress.
   */
  async function setTotalFileSize(
    formData: FormDataForUpload[],
  ): Promise<void> {
    const formDataArray = formData
      .map((data) => [data.file, data.renditionFile].filter(isDefined))
      .flat();
    const totalSize = await getTotalFormDataSize(formDataArray);
    totalFileSize.value = totalSize;
    remainingFileSize.value = totalSize;
  }

  function getDocumentOidByOriginalFileName(originalFileName: string): string {
    const document = uploadUpdateDependencies.value?.documents.find(
      (doc) => doc.originalFileName === originalFileName,
    );
    if (!document) {
      throw new Error(
        `Document with original file name ${originalFileName} not found.`,
      );
    }
    return document.documentOid;
  }

  return {
    uploadFilesAndFinilize,
    uploadProgress,
    fileUploadStatuses,
  };
}
